<template>
  <div class="session-card" :class="{ 'inactive': sessionDeleted }">
    <div class="session-card__details">
      <span class="detail__icon">
        <i v-if="isMobile" class="fas fa-mobile"></i>
        <i v-else class="fas fa-desktop"></i>
      </span>
      <p class="detail__text">{{ session.device.name }}</p>
      <span class="detail__icon"><i class="fas fa-fingerprint"></i></span>
      <p class="detail__text">{{ session.device.uuid }}</p>
      <span class="detail__icon"><i class="fas fa-calendar-day"></i></span>
      <p class="detail__text">{{ dateFormat(session.acquiredDate) }}</p>
    </div>
    <!-- Interactive buttons -->
    <div class="session-card__actions">
      <div v-show="!attemptToDelete && !deletingSession && !sessionDeleted" class="action delete" title="Cerrar sesión" @click="setAttemptToDelete(true)">
        <i class="far fa-trash-alt"></i>
      </div>
      <div v-show="attemptToDelete && !deletingSession && !sessionDeleted" class="action cancel" title="Cancelar el cierre de sesión"
        @click="setAttemptToDelete(false)">
        <i class="fas fa-times"></i>
      </div>
      <div v-show="attemptToDelete && !deletingSession && !sessionDeleted" class="action confirm" title="Confirmar el cierre de sesión"
        @click="closeSession(session.id)">
        <i class="fas fa-check"></i>
      </div>
      <div v-if="deletingSession && !responseOnDelete" class="action d-flex justify-content-center">
        <div class="spinner-border spinner-border-sm small text-secondary" role="status"></div>
      </div>
      <div v-show="attemptToDelete && deletingSession && responseOnDelete" class="action feedback-deleted">
        <i v-show="sessionDeleted" class="far fa-check-circle"></i>
        <i v-show="errorOnDelete" class="far fa-exclamation-circle"></i>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import DateFormat from '@/services/DateFormat'
import AuthService from '@/services/support/AuthService'
import LogService, { ToString } from '@/services/support/LogService'
import Vue from 'vue'
export default Vue.extend({
  props: {
    session: {
      type: Object,
      required: true
    },
    userCredentials: {
      type: Object
    }
  },
  data () {
    return {
      EVENT_TIME: 750, // milliseconds
      MOBILE_UUID_SIZE: 16,
      attemptToDelete: false as boolean,
      deletingSession: false as boolean,
      responseOnDelete: 0 as number // 0: init, 1: success, 2: error
    }
  },
  mounted () {
    this.initCard()
  },
  computed: {
    isMobile (): boolean {
      return this.session.device.uuid.length <= this.MOBILE_UUID_SIZE
    },
    sessionDeleted (): boolean {
      return this.responseOnDelete === 1
    },
    errorOnDelete (): boolean {
      return this.responseOnDelete === 2
    }
  },
  watch: {
  },
  methods: {
    initCard (): void {
      this.attemptToDelete = false
      this.deletingSession = false
      this.setResponseOndelete(0)
    },
    /**
     * Do a request to delete a session by its ID.
     *
     * @param sessionId id of the session to delete
     */
    closeSession (sessionId: number): void {
      this.deletingSession = true
      AuthService.deleteUserSession(sessionId, this.userCredentials)
        .then(() => {
          this.setResponseOndelete(1)
          setTimeout(() => {
            this.$emit('remove-session', sessionId)
          }, this.EVENT_TIME)
        })
        .catch((error) => {
          LogService.error(`Error en liberación de sesión: ${ToString(error.response ?? error)}`)
          this.setResponseOndelete(2)
        })
        .finally(() => {
          setTimeout(() => {
            this.initCard()
          }, this.EVENT_TIME * 4)
        })
    },
    /**
     * Set the 'value' in the 'attemptToDelete' variable
     * @param value to set
     */
    setAttemptToDelete (value: boolean): void {
      this.attemptToDelete = value
    },
    /**
     * Set the 'state' in the 'responseOnDelete' variable
     *    0: initial state
     *    1: successful state
     *    2: failed state
     * @param state to set
     */
    setResponseOndelete (state: number): void {
      this.responseOnDelete = state
    },
    /**
     * Return a string date in local format
     *
     * @param date ISO string date to work with
     */
    dateFormat (date: string): string {
      const localDate = new DateFormat(new Date(date))
      return localDate.toDateTimeString()
    }
  }
})
</script>

<style lang="scss" scoped>
$card-bg: #e9ecef;
$cancel-action: #ced4da;
$cancel-action-hover: #b9c2ca;
$cancel-icon: #6c757d;
$delete-session: #f8d7da;
$delete-session-hover: #f3babe;
$delete-icon: #dc3545;
$confirm-action: #b8daff;
$confirm-action-hover: #99caff;
$confirm-icon: #004085;
$transition-time: 0.75s;

.session-card {
  display: grid;
  grid-template-columns: calc(100% - 48px) 48px;
  width: 100%;
  height: auto;
  background-color: var(--light);
  border-radius: 4px;
  border: 1px solid $card-bg;;
  margin: 0 0 8px 0;

  &.inactive {
    animation: cardClosed;
    animation-duration: $transition-time;
    animation-iteration-count: 1;
    background-color: var(--danger);
    .session-card__details {
      transition-duration: $transition-time;
      padding: 0 8px 0 12px;
    }
  }

  .session-card__details {
    position: relative;
    display: grid;
    grid-template-columns: 24px auto; // first column is for the icons
    grid-template-rows: repeat(3, 1fr);
    align-items: center;
    width: auto;
    max-width: 310px;
    height: auto;
    padding: 8px 8px 8px 12px;
    overflow-y: hidden;

    .detail__icon {
      width: 100%;
      text-align: center;
    }

    .detail__text {
      margin: 0;
      font-size: small;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }

  .session-card__actions {
    justify-self: end;
    display: flex;
    z-index: 2;
    overflow-y: hidden;

    .action {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 48px;
      height: 100%;
      background-color: transparent;
      transition-duration: 0.25s;

      &.delete {
        background-color: $delete-session;
        color: $delete-icon;

        &:hover {
          background-color: $delete-session-hover;
        }
      }

      &.cancel {
        border-right: 1px solid $cancel-action-hover;
        background-color: $cancel-action;
        color: $cancel-icon;

        &:hover {
          background-color: $cancel-action-hover;
        }
      }

      &.confirm {
        background-color: $confirm-action;
        color: $confirm-icon;

        &:hover {
          background-color: $confirm-action-hover;
        }
      }
    }
  }
}
.session-card:last-child {
  margin: 0;
}
@keyframes cardClosed {
  from {
    height: 100%;
    background-color: var(--danger);
  }
  to {
    height: 0%;
  }
}
</style>
